import { createFileRoute, Link } from "@tanstack/react-router";
import type {} from "@tanstack/react-start";

// Map of old URL paths (without leading slash) → new paths.
// Used both by the SSR server handler (301 redirect) and the client
// component (client-side redirect for SPA navigations).
const REDIRECTS: Record<string, string> = {
  // City pages, unify under /elektriker-{by}
  "amager": "/elektriker-amager",
  "bagsvaerd": "/elektriker-bagsvaerd",
  "herlev-2": "/elektriker-herlev",
  "herlev": "/elektriker-herlev",
  "indre-by": "/elektriker-indre-by",
  "kastrup": "/elektriker-kastrup",
  "nordvest": "/elektriker-nordvest",
  "sydhavn": "/elektriker-sydhavn",

  // Services / pages, shorter, keyword-focused
  "priser-privat-bestil-elektriker-pris": "/priser-privat",
  "indhent-tilbud": "/tilbud",
  "om-elektriker": "/om-os",
  "gron-energi": "/groen-energi",
  "energi-optimering": "/energioptimering",

  // Blog posts, move under /blog/{slug}, drop dates
  "2022.12.17.prisen-pa-elektricitet": "/blog/prisen-paa-elektricitet",
  "2022/12/17/prisen-pa-elektricitet": "/blog/prisen-paa-elektricitet",
  "2022.12.17.skal-jeg-skifte-mit-oliefyr-ud-med-en-varmepumpe-i-mit-hus":
    "/blog/oliefyr-til-varmepumpe",
  "2022/12/17/skal-jeg-skifte-mit-oliefyr-ud-med-en-varmepumpe-i-mit-hus":
    "/blog/oliefyr-til-varmepumpe",
  "2022.12.27.hfi-vs-hpfi-relae": "/blog/hfi-vs-hpfi-relae",
  "2022/12/27/hfi-vs-hpfi-relae": "/blog/hfi-vs-hpfi-relae",
  "2022.12.29.bruger-et-fjernsyn-meget-strom": "/blog/fjernsyn-stroemforbrug",
  "2022/12/29/bruger-et-fjernsyn-meget-strom": "/blog/fjernsyn-stroemforbrug",
  "2022.12.29.er-det-farligt-at-fa-strom": "/blog/er-stroem-farligt",
  "2022/12/29/er-det-farligt-at-fa-strom": "/blog/er-stroem-farligt",
  "2022.12.29.er-led-energivenligt": "/blog/er-led-energivenligt",
  "2022/12/29/er-led-energivenligt": "/blog/er-led-energivenligt",
};

function lookupRedirect(splat: string | undefined): string | null {
  if (!splat) return null;
  const key = splat.replace(/\/+$/, "");
  return REDIRECTS[key] ?? null;
}

export const Route = createFileRoute("/$")({
  server: {
    handlers: {
      GET: async ({ params }) => {
        const splat = (params as { _splat?: string })._splat;
        const target = lookupRedirect(splat);
        if (target) {
          return new Response(null, {
            status: 301,
            headers: { Location: target },
          });
        }
        return new Response("Not found", { status: 404 });
      },
    },
  },
  beforeLoad: ({ params }) => {
    const splat = (params as { _splat?: string })._splat;
    const target = lookupRedirect(splat);
    if (target) {
      throw new Response(null, {
        status: 301,
        headers: { Location: target },
      });
    }
  },
  component: NotFoundPage,
});

function NotFoundPage() {
  return (
    <main className="mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center px-6 text-center">
      <p className="text-sm font-semibold uppercase tracking-wider text-primary">404</p>
      <h1 className="mt-3 text-3xl font-semibold text-[var(--ink)]">Siden blev ikke fundet</h1>
      <p className="mt-3 text-muted-foreground">
        Den side du leder efter findes ikke længere. Gå tilbage til forsiden eller se vores ydelser.
      </p>
      <div className="mt-6 flex gap-3">
        <Link to="/" className="inline-flex h-11 items-center rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground hover:brightness-95">
          Til forsiden
        </Link>
        <Link to="/ydelser" className="inline-flex h-11 items-center rounded-md border border-border px-5 text-sm font-semibold hover:bg-muted">
          Se ydelser
        </Link>
      </div>
    </main>
  );
}
